<template>
  <ModalCard
    :class="customClass"
    :data-e2e="dataE2e"
    :close-in-modal="canShowCloseInModal"
    :close-confirm-fn="closeModal"
  >
    <template #header v-if="showHeader">
      <div
        class="flow-modal-header is-flex is-justify-content-space-between is-align-items-center is-full-width"
        :class="{ 'has-sidepanel-spacing': hasSidePanelSpacing }"
        :data-e2e="`${FormCypressAttribute.views.HEADER_ID}${formId}`"
      >
        <div v-if="canShowSidePanel && sidePanelComponent">
          <SidePanel
            :config="service.options.sidePanelConfig"
            @on-exit="cancel"
            @on-toggle-side-panel="toggleSidePanel"
          >
            <template #content>
              <component :is="sidePanelComponent" :service="service" />
            </template>
          </SidePanel>
        </div>
        <div v-else>
          <div v-if="headerLeftComponent" class="m-b-10 m-0-mobile">
            <component :is="headerLeftComponent" />
          </div>
          <div v-show="!hideCancel">
            <div
              class="is-link cancel-link"
              @click="cancel"
              :data-e2e="FormCypressAttribute.buttons.MODAL_CANCEL"
            >
              <span class="icon cls cls-arrow-back"></span>
              <span class="has-text-weight-semibold is-hidden-mobile m-l-4">
                {{ internalCancelText }}
              </span>
            </div>
          </div>
        </div>
        <div>
          <div v-if="headerRightComponent" class="m-b-10 m-0-mobile">
            <component :is="headerRightComponent" />
          </div>
          <div class="is-flex is-align-items-center">
            <ButtonView
              v-if="actionButtonConfig"
              class="icon-button m-r-5"
              type="is-primary is-light"
              icon-pack="cls"
              :icon-right="actionButtonConfig.iconClass"
              @click="actionButtonConfig.actionFn"
              outlined
              :data-e2e="FormCypressAttribute.buttons.ACTION_BUTTON"
            />
            <component
              v-if="upgradeComponent"
              :class="{ 'm-r-10': faqComponent }"
              :is="upgradeComponent"
            />
            <component v-if="faqComponent" :is="faqComponent" :step="form" />
          </div>
        </div>
      </div>
    </template>
    <template #body>
      <div class="is-flex is-justify-content-center">
        <div
          class="flow-modal-content"
          :class="{ 'add-fade-in': !customSkeleton && fadeIn }"
          :style="{ width: width, ...styleOverrides }"
        >
          <div v-if="isLoading">
            <component v-if="customSkeleton" :is="customSkeleton" />
            <FlowModalSkeleton v-else :is-active="true" />
          </div>
          <LoadingView
            :active="isFlowing"
            :data-e2e="FormCypressAttribute.views.flowFlowingLoader"
          />
          <component
            v-if="isMounted"
            ref="currentStepComponent"
            :is="component"
            :value="form"
            :model-value="form"
            @input="onFormUpdate"
            @update:modelValue="onFormUpdate"
            :service="service"
            @flow-change="onFlowChange"
            @is-flowing="onIsFlowing"
            @submit="submit"
            @submit-add-another="submitAddAnother"
            @cancel="cancel"
            @set-style-overrides="setStyleOverrides"
          />
        </div>
      </div>
    </template>
  </ModalCard>
</template>

<script>
import ModalCard from '/@/modules/core/ui/components/ModalCard.vue';
import ToastService from '/@/modules/core/ui/services/ToastService';
import { BaseFlowFormService } from '/@/modules/core/form/services/BaseFlowFormService';
import { BaseModel } from '/@/modules/core/app/models/abstracts/BaseModel';
import { FormFlowMixin } from '/@/modules/core/form/mixins/FormFlowMixin';
import { ModalBrowserBackButtonMixin } from '/@/modules/core/ui/mixins/ModalBrowserBackButtonMixin';
import { ModalMixin } from '/@/modules/core/ui/mixins/ModalMixin';
import FlowModalSkeleton from '/@/modules/core/form/components/FlowModalSkeleton.vue';
import { FlowModalEvent } from '/@/modules/core/form/flowmodal.events';
import { FormCypressAttribute } from '/@/modules/core/form/constants/FormCypressAttribute';
import Lang from '/@/modules/core/app/services/Lang';
import Logger from '/@/modules/core/app/helpers/Logger';
import ModalService from '/@/modules/core/ui/services/ModalService';
import { LoadComponentService } from '/@/modules/core/app/services/LoadComponentService';
import EventBus from '@/modules/core/app/helpers/EventBus';
import SidePanel from '@/modules/core/SidePanel/components/presentational/SidePanel.vue';
import { BaseFlowModalActionConfig } from '@/modules/core/form/models/BaseFlowModalActionConfig';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';
import LoadingView from '@/modules/core/ui/components/wrappers/LoadingView.vue';
import ProgressView from '@/modules/core/ui/components/wrappers/ProgressView.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'BaseFlowModal',
  mixins: [FormFlowMixin, ModalMixin, ModalBrowserBackButtonMixin],
  components: { ButtonView, SidePanel, FlowModalSkeleton, ModalCard, LoadingView, ProgressView },
  props: {
    service: {
      type: BaseFlowFormService,
      required: true
    },
    formId: {
      type: Number,
      default: null
    },
    customForm: {
      type: BaseModel,
      default: null
    },
    cancelText: {
      type: String
    },
    cancelRoute: {
      type: String,
      default: null
    },
    hideCancel: {
      type: Boolean,
      default: false
    },
    width: {
      type: String,
      default: '40%'
    },
    customSkeleton: {
      type: Object,
      default: null
    },
    fadeIn: {
      type: Boolean,
      default: true
    },
    postSubmit: {
      type: Function,
      default: () => {}
    },
    asyncPostSubmit: {
      type: Function,
      default: async () => {}
    },
    headerLeftComponent: {
      type: Object,
      default: null
    },
    headerRightComponent: {
      type: Object,
      default: null
    },
    customClass: {
      type: String
    },
    actionButtonConfig: {
      type: BaseFlowModalActionConfig
    },
    closeInModal: {
      type: Boolean,
      default: false
    },
    dataE2e: {
      type: String,
      default: () => FormCypressAttribute.views.MODAL
    },
    showHeader: {
      type: Boolean,
      default: true
    }
  },
  static: {
    FormCypressAttribute
  },
  events() {
    return { [FlowModalEvent.CLOSE]: this.closeModal };
  },
  data() {
    return {
      form: null,
      component: null,
      faqComponent: null,
      sidePanelComponent: null,
      upgradeComponent: null,
      isLoading: false,
      isFlowing: false,
      styleOverrides: {},
      isMounted: false,
      internalCancelText: Lang.t('accounting.cancel'),
      isSidePanelOpen: false
    };
  },
  computed: {
    canShowCloseInModal() {
      return !this.faqComponent && this.closeInModal;
    },
    isEditing() {
      return this.formId !== null;
    },
    canShowSidePanel() {
      return this.service.canShowSidePanel();
    },
    hasSidePanelSpacing() {
      return this.isSidePanelOpen && !this.faqComponent;
    }
  },
  methods: {
    onFormUpdate(value) {
      this.form = value;
    },
    async onFlowChange(component) {
      this.component = await LoadComponentService.load(component);
      this.component.methods?.onComponentLoad?.();
      this.loadCancelText();
      this.faqComponent = await LoadComponentService.load(
        this.service.getFaqComponent(this.component)
      );
    },
    loadCancelText() {
      this.internalCancelText =
        this.component.methods?.getCancelText?.() ?? this.cancelText ?? Lang.t('accounting.cancel');
    },
    onIsFlowing(value) {
      this.isFlowing = value;
    },
    async cancel() {
      if (this.$refs.currentStepComponent?.cancel) {
        await this.$refs.currentStepComponent.cancel();
        return;
      }
      if (this.cancelRoute && this.$router.currentRoute.value.name !== this.cancelRoute) {
        await this.$router.push({ name: this.cancelRoute });
      }
      this.closeModal();
    },
    setStyleOverrides(styles = {}) {
      this.styleOverrides = styles;
    },
    help() {
      Logger.log("Help me Pascal, you're my only hope!");
    },
    async submit() {
      if (this.isFlowing) {
        return;
      }

      this.isFlowing = true;
      try {
        if (this.$refs.currentStepComponent?.preSave) {
          await this.$refs.currentStepComponent.preSave();
        }
        const newData = await this.service.submitForm(this.form);

        await this.asyncPostSubmit();
        this.postSubmit(newData);

        const onSaveMessage = this.service.getSaveMessage(this.form);
        if (onSaveMessage) {
          ToastService.show(onSaveMessage);
        }

        this.closeModal();

        this.$nextTick(async () => {
          const modalOptions = await this.service.getPostSubmitModalOptions(newData);
          if (modalOptions) {
            ModalService.show({
              parent: this.$parent,
              ...modalOptions
            });
          }
        });
      } finally {
        this.isFlowing = false;
      }
    },
    async submitAddAnother() {
      await this.submit();
      EventBus.signal(FlowModalEvent.ADD_ANOTHER, this.form);
    },
    async onPopState() {
      if (this.$refs.currentStepComponent?.back) {
        await this.$refs.currentStepComponent.back();
        return;
      }
      this.closeModal();
    },
    closeModal() {
      this.close(this.service.getModalCloseDelay(), () => this.service.onModalClose());
    },
    toggleSidePanel(panelState) {
      this.isSidePanelOpen = panelState;
      this.service.toggleSidePanel(panelState);
    }
  },
  async mounted() {
    this.isLoading = true;
    try {
      if (this.isEditing) {
        this.form = await this.service.getForm(this.formId);
        this.component = await LoadComponentService.load(this.service.getLastComponent(this.form));
      } else {
        this.component = await LoadComponentService.load(this.service.getFirstComponent());
        this.form = this.customForm ?? (await this.service.getNewForm());
      }
      this.component.methods?.onComponentLoad?.();
      this.loadCancelText();
      this.faqComponent = await LoadComponentService.load(
        this.service.getFaqComponent(this.component)
      );
      this.upgradeComponent = await LoadComponentService.load(this.service.getUpgradeComponent());
      if (this.canShowSidePanel) {
        this.sidePanelComponent = await LoadComponentService.load(
          this.service.getSidePanelComponent()
        );
        this.isSidePanelOpen = this.service.options.sidePanelConfig?.open ?? false;
      }
    } finally {
      this.isLoading = false;
      this.isMounted = true;
    }
  }
};
</script>

<style scoped>
.flow-modal-header :deep(.button:not(.panel-content .button)) {
  max-height: var(--ce-40px) !important;
  margin: 0 !important;
}

.flow-modal-header.has-sidepanel-spacing {
  margin-top: 2rem !important;
}

@media (max-width: CE_MOBILE) {
  :deep(section.modal-card-body) {
    margin-top: unset !important;
  }
  .flow-modal-content {
    margin-top: 0 !important;
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
}
</style>
